import { computed, ComputedRef, nextTick, Ref, ref } from "vue"
import {
  filterStaffByName,
  PatientConversationStaffOption
} from "@/modules/patient-conversations/composables/staff-directory"
import {
  findMentionTrigger,
  hasMention,
  mentionText,
  MentionTrigger,
  toWireContent,
  withoutMention
} from "@/modules/patient-conversations/utils/patient-conversation-mentions"

interface NotePagesOptions {
  /** The composer text, which shows pages as "@Full Name". */
  content: Ref<string>
  /** Whether pages are allowed right now (internal note mode). */
  enabled: ComputedRef<boolean>
  staff: Ref<Array<PatientConversationStaffOption> | undefined>
  isStaffLoading: Ref<boolean>
  /** The native textarea, for the caret and refocusing after a pick. */
  textarea: () => HTMLTextAreaElement | undefined
}

/**
 * Pages in an internal note (TDP §5.2.6). The textarea shows "@Full Name"; the people chosen from the "@" picker
 * are remembered here and their names become `@[usr_…]` tokens when the note is sent. A name typed by hand
 * without the picker is plain text.
 */
export function useNotePages({ content, enabled, staff, isStaffLoading, textarea }: NotePagesOptions) {
  const chosenStaff = ref<Array<PatientConversationStaffOption>>([])
  const caret = ref<number>(0)
  const isInputFocused = ref<boolean>(false)
  // Where the "@" of a picker the user escaped sits; typing a new "@" opens the picker again
  const dismissedTriggerStart = ref<number | undefined>()

  /** The chosen people whose "@Full Name" is still in the text, in the order they were picked. */
  const pagedStaff = computed<Array<PatientConversationStaffOption>>(() =>
    chosenStaff.value.filter((person) => hasMention(content.value, person))
  )

  // An "@" at the start of a word before the caret that is not already a chosen person's name
  const trigger = computed<MentionTrigger | undefined>(() => {
    if (!enabled.value) return undefined
    const found = findMentionTrigger(content.value, caret.value)
    if (!found || found.start === dismissedTriggerStart.value) return undefined
    if (pagedStaff.value.some((person) => hasMention(`@${found.query}`, person))) return undefined
    return found
  })

  // The picker closes once a space follows a word that matches nobody: the user has moved on to the next word
  const isPickerOpen = computed<boolean>(() => {
    if (!trigger.value || !isInputFocused.value) return false
    const hasMatches = filterStaffByName(staff.value ?? [], trigger.value.query).length > 0
    return hasMatches || isStaffLoading.value || !trigger.value.query.endsWith(" ")
  })

  function trackCaret(): void {
    caret.value = textarea()?.selectionStart ?? content.value.length
  }

  function onInput(): void {
    trackCaret()
    // A freshly typed "@" always gets the picker, even where an earlier one was escaped
    if (content.value[caret.value - 1] === "@") dismissedTriggerStart.value = undefined
  }

  function onFocus(): void {
    isInputFocused.value = true
  }

  function onBlur(): void {
    isInputFocused.value = false
  }

  function dismissPicker(): void {
    dismissedTriggerStart.value = trigger.value?.start
  }

  /** Replaces the "@query" being typed with the chosen person's "@Full Name " and puts the caret after it. */
  async function insert(staffId: string | null): Promise<void> {
    const active = trigger.value
    const person = (staff.value ?? []).find((candidate) => candidate.id === staffId)
    if (!active || !person) return

    const insertion = `${mentionText(person)} `
    content.value =
      content.value.slice(0, active.start) + insertion + content.value.slice(active.start + 1 + active.query.length)
    chosenStaff.value = [...chosenStaff.value.filter((chosen) => chosen.id !== person.id), person]

    await nextTick()
    const element = textarea()
    const position = active.start + insertion.length
    element?.focus()
    element?.setSelectionRange(position, position)
    caret.value = position
  }

  function remove(person: PatientConversationStaffOption): void {
    content.value = withoutMention(content.value, person)
    chosenStaff.value = chosenStaff.value.filter((chosen) => chosen.id !== person.id)
    trackCaret()
  }

  /** The text as the server stores it, with each page as its `@[usr_…]` token. */
  function toWire(text: string): string {
    return toWireContent(text, pagedStaff.value)
  }

  function reset(): void {
    chosenStaff.value = []
    dismissedTriggerStart.value = undefined
  }

  return {
    pagedStaff,
    trigger,
    isPickerOpen,
    trackCaret,
    onInput,
    onFocus,
    onBlur,
    dismissPicker,
    insert,
    remove,
    toWire,
    reset
  }
}
